import { useState, useEffect, useCallback } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { getCurrentUserId, isBandMember } from '../_helpers/bandUtils.js';
import config from '../config.js';

/**
 * A failure as the components render it: `{ text }` when the backend worded
 * the message, else `{ key }` of a catalog message the component passes to
 * `t()` — the hooks never phrase English themselves (ENG-380).
 *
 * "The backend worded it" follows fetchWrapper's convention (see `serverWords`
 * in streamSource.js): its `message` is the response body when there was one
 * and the English `statusText` otherwise, so a bare "Not Found" / "Internal
 * Server Error" is a fallback, not wording. A network failure (a `TypeError`
 * from `fetch`, no `status`) is the browser's English and never shown either.
 */
const failure = (error, key) => {
  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  return message && error.status && message !== error.statusText ? { text: message } : { key };
};

/**
 * Custom hook for managing user roles in a specific band
 * @param {string} bandId - The band ID to check roles for
 * @returns {Object} Role information and management functions
 */
// Fix hot reload export issue
export const useUserRole = (bandId) => {
  const fetchWrapper = useFetchWrapper();
  const [roleData, setRoleData] = useState({
    role: null,
    isLoading: true,
    error: null,
    isMember: false,
    isModerator: false,
    bandInfo: null
  });

  const userId = getCurrentUserId();

  const fetchUserRole = useCallback(async () => {
    // An ID that isn't a positive integer names no band, so there is no role
    // to ask about (ENG-525: /performers/abc asked for /bands/abc).
    if (!/^[1-9]\d*$/.test(String(bandId ?? '')) || !userId) {
      setRoleData({
        role: null,
        isLoading: false,
        error: null,
        isMember: false,
        isModerator: false,
        bandInfo: null
      });
      return;
    }

    setRoleData(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // The band tells the caller their own role as viewer_role (member,
      // moderator, follower or none). It used to be worked out by also
      // fetching /bands/{id}/moderators, which is member-only: every
      // non-member got a 403 there, and a 403 used to end the session, so
      // plain listeners and real moderators were logged out (ENG-494).
      const bandInfo = await fetchWrapper.get(`${config.apiBaseUrl}/bands/${bandId}`);
      const viewerRole = bandInfo?.viewer_role;
      // A backend that predates viewer_role still lists the members.
      const isUserMember = viewerRole ? viewerRole === 'member' : isBandMember(bandInfo, userId);
      const isUserModerator = !isUserMember && viewerRole === 'moderator';

      setRoleData({
        role: isUserMember ? 'member' : isUserModerator ? 'moderator' : 'user',
        isLoading: false,
        error: null,
        isMember: isUserMember,
        isModerator: isUserModerator,
        bandInfo
      });

    } catch (error) {
      console.error('Error fetching user role:', error);
      setRoleData({
        role: null,
        isLoading: false,
        error: failure(error, 'moderators.errors.roleLoadFailed'),
        isMember: false,
        isModerator: false,
        bandInfo: null
      });
    }
  }, [bandId, userId, fetchWrapper]);

  useEffect(() => {
    fetchUserRole();
  }, [fetchUserRole]);

  const refreshRole = useCallback(() => {
    fetchUserRole();
  }, [fetchUserRole]);

  return {
    ...roleData,
    refreshRole
  };
};

/**
 * Hook for managing moderators of a band (member-only functionality)
 * @param {string} bandId - The band ID
 * @returns {Object} Moderator management functions and state; `error` and the
 *   `error` of a failed add/remove are `{ text }` | `{ key }` (see `failure`)
 */
export const useModerators = (bandId) => {
  const fetchWrapper = useFetchWrapper();
  const [moderators, setModerators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchModerators = useCallback(async () => {
    if (!bandId) {
      setModerators([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWrapper.get(`${config.apiBaseUrl}/bands/${bandId}/moderators`);
      setModerators(response || []);
    } catch (error) {
      console.error('Error fetching moderators:', error);
      setError(failure(error, 'moderators.errors.loadFailed'));
      setModerators([]);
    } finally {
      setIsLoading(false);
    }
  }, [bandId, fetchWrapper]);

  const addModerator = useCallback(async (userId) => {
    try {
      await fetchWrapper.post(`${config.apiBaseUrl}/bands/${bandId}/moderators`, {
        user_id: userId
      });
      
      // Refresh the moderators list
      await fetchModerators();
      
      return { success: true };
    } catch (error) {
      console.error('Error adding moderator:', error);
      return { 
        success: false, 
        error: failure(error, 'moderators.errors.addFailed') 
      };
    }
  }, [bandId, fetchWrapper, fetchModerators]);

  const removeModerator = useCallback(async (userId) => {
    try {
      await fetchWrapper.delete(`${config.apiBaseUrl}/bands/${bandId}/moderators/${userId}`);
      
      // Refresh the moderators list
      await fetchModerators();
      
      return { success: true };
    } catch (error) {
      console.error('Error removing moderator:', error);
      return { 
        success: false, 
        error: failure(error, 'moderators.errors.removeFailed') 
      };
    }
  }, [bandId, fetchWrapper, fetchModerators]);

  useEffect(() => {
    fetchModerators();
  }, [fetchModerators]);

  return {
    moderators,
    isLoading,
    error,
    addModerator,
    removeModerator,
    refreshModerators: fetchModerators
  };
};